import { useEffect, useState } from "react";

// -------- Session Storage Class --------
export class SessionStorageUtil {
  static setItem(key: string, value: string) {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      console.error(`Error setting sessionStorage key "${key}":`, error);
    }
  }

  static getItem(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      console.error(`Error getting sessionStorage key "${key}":`, error);
      return null;
    }
  }

  static removeItem(key: string) {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing sessionStorage key "${key}":`, error);
    }
  }

  static clear() {
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error("Error clearing sessionStorage:", error);
    }
  }
}

// -------- Local Storage Class --------
export class LocalStorageUtil {
  static setItem(key: string, value: string) {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  }

  static getItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error(`Error getting localStorage key "${key}":`, error);
      return null;
    }
  }

  static removeItem(key: string) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error);
    }
  }

  static clear() {
    try {
      localStorage.clear();
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }
  }
}

// All ephemeral UI indices (table scroll, pagination, sort) live inside one
// sessionStorage key, so they clear together when the tab/session ends.
const VF_SESSION_KEY = "vf:session";

type SessionStateShape = Record<string, unknown>;

export class VfSessionStore {
  private static read(): SessionStateShape {
    try {
      const raw = SessionStorageUtil.getItem(VF_SESSION_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === "object"
        ? (parsed as SessionStateShape)
        : {};
    } catch {
      return {};
    }
  }

  static get<T>(field: string, fallback: T): T {
    const state = VfSessionStore.read();
    return field in state ? (state[field] as T) : fallback;
  }

  static set(field: string, value: unknown) {
    const state = VfSessionStore.read();
    state[field] = value;
    SessionStorageUtil.setItem(VF_SESSION_KEY, JSON.stringify(state));
  }
}

// `useState` drop-in persisted in the shared `vf:session` store under `field`.
export function useSessionStorageState<T>(field: string, defaultValue: T) {
  const [value, setValue] = useState<T>(() =>
    VfSessionStore.get<T>(field, defaultValue)
  );

  useEffect(() => {
    VfSessionStore.set(field, value);
  }, [field, value]);

  return [value, setValue] as const;
}

